// ─────────────────────────────────────────────────────────────────────────────
// AgentRunControl — THE OFF SWITCH, ON EVERY SURFACE THE OWNER LANDS ON.
//
// OWNER RULING 2026-08-31: "IT CANNOT ONLY LIVE IN THE CHAT COMPOSER. IF THE
// SURFACE IS SLOW, REACHING AN AGENT'S CHAT TO SWITCH HIM OFF IS THE THING THAT
// IS SLOW." So one control, three mounts:
//
//     the composer row          ComposerAgentControls   (where he already was)
//     the agent's header bar    AgentTabBar, beside DASHBOARD and CHAT
//     a FULL-SURFACE agent's    MissionControl's own agent bar — same place,
//       header bar                different component (see below)
//     the domain surface        MissionControl's rail — THE DOT ONLY
//
// ── TWO HEADER BARS, AND MOUNTING ONE OF THEM WAS NOT ENOUGH ──────────────
// The first attempt put this in `AgentTabBar` and Buck never saw it, because
// GROWTH is a FULL_SURFACE domain: his dashboard IS Mission Control, so
// `AgentViewScreen` — and the shared tab bar with it — is only ever mounted
// when he asks for CHAT by name. Mission Control renders its OWN agent bar,
// and says so at the site: "Rendered with this surface's sampled palette
// rather than the shared AgentTabBar." Two bars, and the control has to be in
// both. THE SYMPTOM WAS AN ABSENT ELEMENT, WHICH IS NOT THE SAME FAULT AS AN
// UNREADABLE ONE — a control that could not read would have said so.
//
// ── IT CAME OUT OF THE RAIL CARD (owner ruling 2026-08-31) ────────────────
// The first build put the whole control INSIDE Buck's rail row and took that
// row to roughly four times the height of every other agent's, pushing Delta
// and Echo down and cutting Osiris off. THE RAIL IS A NAVIGATION LIST AND EVERY
// ROW MUST BE THE SAME SHAPE. So the rail keeps a DOT — which costs no vertical
// space at all, because the row already had one — and the control moved to the
// header bar that already carries DASHBOARD and CHAT on one row.
//
// ── AND IT DOES NOT RENDER PROSE ANYWHERE ─────────────────────────────────
// The same build printed eight lines of justification into that rail. "A
// CONTROL EXPLAINS ITSELF IN A FEW WORDS OR NOT AT ALL"; the reasoning belongs
// in the source. The detail block is GONE — `view.detail` is a few words and it
// reaches the screen only as the `title` tooltip.
//
// SAME CONTROL, SAME STATE, ONE AUTHOR — `state/agentRunStore`. Not one
// component copied three times, and not three components asking the box the same
// question: a second answer to "is this agent working" is the fault this whole
// build is a correction of.
//
// ── THE HOOK IS THE CONTROL; THE CHIP IS ONLY ITS PAINT ────────────────────
// `useAgentRun` carries every decision (all of it pure, in `lib/runControl`) and
// the composer takes the hook alone — because the composer already owns a
// refusal panel and a second one beside it would be two places to look. The
// dashboard and rail take `<AgentRunControl>`, which is the same hook plus a
// surface of its own. There is no third path.
//
// ── CHEAP BY CONSTRUCTION, BECAUSE THE STARVED BOX IS THE POINT ────────────
//   • no fetch on mount for the harness agent — his state rides the 2.88 ms
//     shared route, never the 2653 ms one (measured; see the store's header)
//   • ONE poll for the whole app, refcounted, however many mounts exist
//   • a rail listing ten agents makes ZERO per-row requests
//   • no gradient, no blur, no shadow that re-renders per frame — a chip
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useSyncExternalStore } from 'react';
import {
  subscribe, getEntry, getRunner, getRunnerFailures, ensureLoop, holdRunnerPoll,
  stop as storeStop, arm as storeArm, isHarnessAgent, RETRIES_BEFORE_OFFERING,
  runnerSourceFor, getRunnerOf, holdRunnerPollOf,
} from '../../state/agentRunStore';
import { resolveRunControl, RunControlView, RunTone } from '../../lib/runControl';
import type { RunnerStatus } from '../../api/growthRunner';
import { buckStrip, BuckStrip, coolingText } from '../../lib/buckLane';

export interface UseAgentRun {
  view: RunControlView;
  /** The live line — WHAT HE IS DOING, not merely whether he is switched on.
   *  Null for the nine agents with no work harness, who have no walk to report. */
  strip: BuckStrip | null;
  isHarness: boolean;
  press: () => void;
  /** Fires `view.alsoOffers`. A no-op when the view offers only one action. */
  pressAlso: () => void;
}

/**
 * THE CONTROL, HEADLESS.
 *
 * `wantStrip` is a REQUEST, not a promise — the strip renders only where there
 * is a harness to report on. A caller asking for it on Spencer gets null rather
 * than an empty box, which is `buckLane`'s own narrowness carried forward.
 *
 * `mayRead` IS THE FAN-OUT GUARD, AND IT IS HERE RATHER THAN AT THE CALL SITE.
 * A LIST surface mounting one of these per row would fire one
 * `GET /api/rexy/agent-loop` per agent — ten hub-blocking reads to draw a
 * sidebar, on the box this control exists to unstarve. A list passes false and
 * renders from the ONE shared route; a focused surface (one agent, on purpose)
 * passes true. The harness agent never reads that route either way.
 */
export function useAgentRun(
  domain: string, agent: string | null, wantStrip = true, mayRead = true,
): UseAgentRun {
  const a = agent || '';
  const isHarness = isHarnessAgent(domain, a);

  // MEMOISED, AND NOT AS TIDINESS. `useSyncExternalStore` re-subscribes whenever
  // the getSnapshot IDENTITY changes, so an inline arrow would tear down and
  // rebuild the subscription on every render of every mounted control. The
  // snapshot it returns is stable by reference between changes — that is the
  // store's half — and this is the other half.
  const readEntry = useCallback(() => getEntry(domain, a), [domain, a]);
  const entry = useSyncExternalStore(subscribe, readEntry);
  const runner = useSyncExternalStore(subscribe, getRunner);
  const failures = useSyncExternalStore(subscribe, getRunnerFailures);

  // The one-shot read for an agent WITHOUT a harness. `ensureLoop` is a no-op
  // for the harness agent, so this effect does not need to know which is which.
  useEffect(() => {
    if (!mayRead) return;
    if (!domain || !a) return;
    ensureLoop(domain, a);
  }, [domain, a, mayRead]);

  // THE SHARED POLL, HELD ONLY WHILE SOMETHING NEEDS IT. The refcount lives in
  // the store, so three mounted controls hold one interval between them.
  const needsRunner = isHarness || (wantStrip && isHarness);
  useEffect(() => {
    if (!needsRunner) return;
    return holdRunnerPoll();
  }, [needsRunner]);

  const view = resolveRunControl({
    loop: entry.loop,
    // ALWAYS THE ENTRY'S PHASE, AND FOR THE HARNESS AGENT THAT IS HONESTLY
    // 'unread' — the store never reads him on this route at all.
    //
    // THIS LINE USED TO BE `isHarness ? runnerRead : entry.read` AND THE
    // MUTATION PROOF COULD NOT CONVICT IT (fault 14), which is standing rule 4:
    // a guard no mutation can convict is either wrong or untested. It was
    // neither — it was INERT. `resolveRunControl` resolves the harness path
    // entirely from `runner`; `read` is never consulted on it. So the ternary
    // was decoration implying a dependency that does not exist, and it is gone
    // rather than kept for looks. R16 asserts the property outright.
    read: entry.read,
    write: entry.write,
    writeWanted: entry.writeWanted,
    runner,
    isHarness,
    name: a.toUpperCase(),
    // THE RETRY LADDER'S OWN COUNT, from whichever read this agent actually
    // uses — the shared runner route for the harness agent, this entry's own
    // for everyone else. Reading the wrong one is how the other nine ended up
    // waiting for ever on a counter that only ever moved for Buck.
    failures: isHarness ? failures : entry.failures,
    retriesBeforeOffering: RETRIES_BEFORE_OFFERING,
  });

  const act = useCallback((what: 'stop' | 'arm') => {
    if (!domain || !a) return;
    if (what === 'stop') storeStop(domain, a);
    else storeArm(domain, a);
  }, [domain, a]);

  const press = useCallback(() => {
    if (!view.actionable || !view.action) return;
    act(view.action);
  }, [act, view.actionable, view.action]);

  // THE SECOND ACTION, AND ONLY WHERE THE VIEW ASKS FOR ONE. It exists for the
  // read-failed-and-retries-spent state, where neither RUN nor STOP can be
  // chosen for the owner and he needs both — the state in which the screen was
  // useless to him and the ON switch was an SSH command.
  const pressAlso = useCallback(() => {
    if (!view.alsoOffers) return;
    act(view.alsoOffers);
  }, [act, view.alsoOffers]);

  return {
    view,
    strip: isHarness && wantStrip ? buckStrip(runner) : null,
    isHarness,
    press,
    pressAlso,
  };
}

// ── PAINT ───────────────────────────────────────────────────────────────────
// THE TONES, AND WHY GREEN IS NOT AMONG THEM.
//
// Owner ruling 2026-08-29 stands: GREEN MEANS "STAYS ON THE BOX" in the composer
// row, where LOCAL ONLY sits green two chips away. This chip mounts in that same
// row, so a green "WALKING" would be asserting the transport claim on a control
// that has nothing to do with transport. WALKING and ARMED are separated the way
// that ruling separated the phone lane — BY TREATMENT: armed is a gold outline,
// walking is the same gold FILLED. One hue, two states, no fifth colour.
//
// The strip below keeps its own green, unchanged: it is in the detail surface,
// not the chip row, and it already shipped that way.
const TONE: Record<RunTone, { fg: string; bg: string; filled: boolean }> = {
  walking:     { fg: '#0a0a0a', bg: '#FFD700', filled: true },
  // STOPPING takes the same amber the write phases take, and that is the point
  // rather than a shortage of colours: all three are "in flight, wait". They
  // are told apart by the LABEL — STOPPING … / STOP SENT / STOPPING — which is
  // the only difference the owner needs while he is watching one of them.
  stopping:    { fg: '#0a0a0a', bg: '#f59e0b', filled: true },
  // HALTED IS AN OUTAGE: danger, FILLED, so it is never read as OFF (a bare
  // red dot) or as STOPPING (amber, in flight).
  halted:      { fg: '#0a0a0a', bg: '#ef4444', filled: true },
  on:          { fg: '#FFD700', bg: 'rgba(255,215,0,0.14)', filled: false },
  off:         { fg: 'inherit', bg: 'transparent', filled: false },
  unread:      { fg: '#f59e0b', bg: 'transparent', filled: false },
  pending:     { fg: '#f59e0b', bg: 'transparent', filled: false },
  unconfirmed: { fg: '#0a0a0a', bg: '#f59e0b', filled: true },
};

const PRESSED = 'inset 0 1px 2px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.06)';

export function RunChip(
  { view, onPress, testId = 'run-chip' }:
  { view: RunControlView; onPress: () => void; testId?: string },
) {
  const t = TONE[view.tone];
  return (
    <button
      type="button"
      data-testid={testId}
      data-tone={view.tone}
      onClick={onPress}
      disabled={!view.actionable}
      aria-pressed={view.on === true}
      title={view.detail || view.label}
      className="px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest
                 border border-rexy-border transition-colors"
      style={{
        color: t.fg === 'inherit' ? undefined : t.fg,
        background: t.bg,
        boxShadow: t.filled || view.on ? PRESSED : undefined,
        opacity: view.actionable ? 1 : 0.55,
        cursor: view.actionable ? 'pointer' : 'default',
        whiteSpace: 'nowrap',
      }}
    >
      {view.label}
    </button>
  );
}

// ── THE RAIL'S STATUS DOT (owner ruling 2026-08-31) ────────────────────────
// "EVERY RAIL ROW ALREADY CARRIES ONE AND THEY ARE ALL GREY." They were: the
// rail tested `a.implementationState === 'live'` while the registry projection
// writes `LIVE_BUILT`, so the comparison could never be true and every dot on
// the surface was the idle one. It was reporting the wrong thing anyway — the
// owner's question is not "was this agent built", it is "is he working".
//
// ── FIVE STATES OUT OF TWO AXES (owner ruling 2026-09-01) ─────────────────
// The owner asked for four and then named a fifth — armed, and stopped of his
// own accord — that could be neither green nor red. FIVE HUES WOULD HAVE BEEN
// FIVE LOOKUPS, and gold beside amber on a 7px dot is one colour, not two. So
// the mapping is DERIVED FROM TWO FACTS instead, and there is nothing to
// memorise:
//
//     FILL = IS A WALK IN FLIGHT       HUE = WHAT THE CONSENT SAYS
//
//   ● green filled   WALKING            armed, and a round is running
//   ◐ amber filled   STOPPING           disarmed, and the round is still running
//   ○ green hollow   ON, NOT WALKING    armed, nothing running
//   ● red filled     OFF                ruled off by the owner
//   ○ grey hollow    NEVER RULED        nobody has decided — or no harness, or
//                                       not read
//
// THE GLOW IS PART OF THE FILL, not decoration: a walk in flight is the only
// thing that glows, so the two hollow states cannot be mistaken for busy ones
// at a glance down a ten-row rail.
//
// AND OFF MOVED FROM GREY TO RED. Grey now means exactly one thing — nobody has
// ruled — which is the split the toggle has always had in the data
// (`has_setting` separate from `enabled`) and which the dot was collapsing.
//
// A DOT THAT CANNOT BE ANSWERED SAYS NOTHING AND DOES NOT SPIN. The hollow grey
// is deliberately the same for "no harness", "never ruled" and "not read":
// none of the three is a claim about the agent, and inventing a difference
// between three kinds of silence would be the fault this whole surface keeps
// closing.
//
// ── AND IT READS THE WORD; IT DOES NOT COMPOSE ONE ────────────────────────
// `stopping` is the ROUTE'S word (see `growthRunner.RunnerState`). Deriving it
// here from `!armed && running` would be a second copy of the rule the route
// composes — which `readState` refuses by name, and which is the fault class
// this build has spent a week removing. Nothing emits it yet; this lights up
// when it does.
//
// TEN ROWS ARE ONE READ. `RunDot` holds the same refcounted poll every other
// mount holds, and an agent with no harness holds nothing and fetches nothing —
// `mayRead` is false and `needsRunner` is false for him.
export type RunDotState = 'walking' | 'stopping' | 'halted' | 'armed' | 'off' | 'silent';

export function runDotState(
  isHarness: boolean, runner: RunnerStatus | null,
): RunDotState {
  if (!isHarness) return 'silent';
  if (!runner || !runner.reachable || runner.state === 'unknown') return 'silent';
  if (runner.state === 'walking') return 'walking';
  if (runner.state === 'stopping') return 'stopping';
  if (runner.state === 'halted') return 'halted';
  if (runner.state === 'armed_not_walking') return 'armed';
  // OFF, and `ruled` is what separates "the owner said no" from "nobody has
  // said anything". Both are off; they are not both a decision.
  return runner.ruled ? 'off' : 'silent';
}

const DOT_STYLE: Record<RunDotState, React.CSSProperties> = {
  walking:  { background: '#22c55e', boxShadow: '0 0 8px #22c55e' },
  stopping: { background: '#f59e0b', boxShadow: '0 0 8px #f59e0b' },
  // HALTED IS A DIFFERENT SHAPE, NOT A DIFFERENT GLOW (owner ruling
  // 2026-09-14). OFF and HALTED are the owner's two red states and they mean
  // opposite things — one is his choice, one is a fault — so at a glance they
  // must not be the same disc. OFF stays a round, still red disc; HALTED is a
  // glowing red DIAMOND, the warning shape, readable at 7px without a tooltip.
  // `scale` keeps the rotated square inside the row's 7px box.
  halted:   { background: '#ef4444', boxShadow: '0 0 8px #ef4444',
              borderRadius: '1px', transform: 'rotate(45deg) scale(0.8)' },
  // HOLLOW IS THE WHOLE STATEMENT: same hue as walking — the consent is
  // unchanged — and no fill, because nothing is running.
  armed:    { background: 'transparent', border: '1px solid #22c55e' },
  off:      { background: '#ef4444' },
  silent:   { background: 'transparent', border: '1px solid #3a4454' },
};

/**
 * THE DOT'S ONE SENTENCE. A FUNCTION RATHER THAN A TABLE, because the armed
 * state has two meanings and the old table could only carry one of them.
 *
 * IT SAID "ON — NOTHING DUE" WHILE 22 COMPANIES WERE DUE (owner ruling
 * 2026-09-01). Armed-and-idle is either "the 60s tick has not landed" or "he
 * stopped himself under the anti-spin rule and the runner is refusing to
 * restart him", and `cooling_until` is the only thing that tells them apart —
 * the runner sets it only when the owner did NOT stop him.
 */
export function runDotTitle(
  state: RunDotState, runner: RunnerStatus | null, now: number = Date.now(),
): string {
  if (state === 'walking') return 'WORKING NOW';
  if (state === 'stopping') {
    return 'STOPPING — FINISHING THE ROUND IN FLIGHT, NEVER KILLED MID-COMPANY';
  }
  if (state === 'armed') {
    const cooling = coolingText(runner?.coolingUntil ?? null, now);
    // A FEW WORDS OR NOTHING. The reason itself is a paragraph and it belongs
    // in the strip, which has a row for it; the dot says WHICH of the two this
    // is and stops there.
    return cooling ? `ON — HE STOPPED HIMSELF · ${cooling}` : 'ON — NOTHING DUE';
  }
  if (state === 'halted') {
    return `HALTED — ${(runner?.halt?.down || 'A LANE').toUpperCase()} DOWN`;
  }
  if (state === 'off') return 'OFF';
  return 'NOT REPORTED';
}

/**
 * The dot alone, for a list row. No chip, no strip, no prose, no height.
 *
 * IT READS WHICHEVER ROUTE ANSWERS FOR THIS AGENT (owner ruling 2026-09-23).
 * Buck's is `/api/growth/runner`; Delta's is `/api/delta/activity`, which speaks
 * Buck's words — so the same `runDotState`, the same `DOT_STYLE`, the same poll.
 * An agent with no route holds nothing and stays the hollow ring.
 */
export function RunDot(
  { domain, agent, size = 7 }: { domain: string; agent: string | null; size?: number },
) {
  const src = runnerSourceFor(domain, agent || '');
  const runner = useSyncExternalStore(subscribe, () => getRunnerOf(src));
  useEffect(() => {
    if (!src) return;
    return holdRunnerPollOf(src);
  }, [src]);

  const state = runDotState(src !== null, runner);
  return (
    <span
      data-testid="run-dot"
      data-run-dot={state}
      title={runDotTitle(state, runner)}
      style={{
        width: size, height: size, borderRadius: '50%',
        boxSizing: 'border-box', flexShrink: 0,
        ...DOT_STYLE[state],
      }}
    />
  );
}

const STRIP_TONE: Record<BuckStrip['tone'], string | undefined> = {
  walking: '#22c55e', stopping: '#f59e0b', halted: '#ef4444', armed: '#FFD700',
  unread: '#f59e0b', off: undefined,
};

export interface AgentRunControlProps {
  domain: string;
  agent: string | null;
  /** Show the live line beside the chip. Ignored where there is no harness. */
  strip?: boolean;
  /** False on a LIST surface. See `mayRead` on the hook — it is the guard that
   *  stops a ten-row rail becoming ten hub-blocking reads. */
  read?: boolean;
  testId?: string;
}

/**
 * THE MOUNTED CONTROL — chip, live line, and the sentence in full.
 *
 * Used by the agent's dashboard and by the domain rail. A REFUSAL IS NEVER
 * SHORTENED TO FIT: whatever will not go in the chip is rendered underneath, in
 * the DOM, never behind a hover a screenshot cannot prove.
 */
export default function AgentRunControl(
  { domain, agent, strip = true, read = true, testId }: AgentRunControlProps,
) {
  const { view, strip: line, press, pressAlso } = useAgentRun(domain, agent, strip, read);
  const base = testId || 'agent-run-control';

  // ── IT NEVER RENDERS NOTHING ON A FAILED LOOKUP (owner ruling 2026-08-31) ──
  // "A control that cannot resolve its agent SAYS SO VISIBLY. Rendering null on
  // a failed lookup is the same class as `implementationState === 'live'` — a
  // condition that could never be true, and nothing said so."
  //
  // THE DISCRIMINATOR IS A PARTIAL PAIR, not an empty one. Neither half present
  // means there is no agent here — the homepage, the CEO sentinel — and silence
  // is the correct answer. ONE half present means a lookup was attempted and
  // failed, which is always a bug, and it is now impossible for that bug to be
  // invisible.
  if (!domain && !agent) return null;
  if (!domain || !agent) {
    return (
      <span
        data-testid={`${base}-unresolved`}
        title={`RUN CONTROL COULD NOT RESOLVE ITS AGENT — domain="${domain}" agent="${agent}"`}
        className="px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest
                   border border-rexy-border"
        style={{ color: 'var(--rexy-danger, #ef4444)', whiteSpace: 'nowrap' }}
      >
        AGENT UNRESOLVED
      </span>
    );
  }

  // ONE BASE, DERIVED HANDLES. The chip used to fall back to RunChip's own
  // default whenever no testId was passed, so the wrapper was
  // `agent-run-control` while its chip was `run-chip` — two naming schemes on
  // one control, and a probe selecting the obvious one measured nothing. Same
  // class as the transport chip needing `data-testid` because `role="switch"`
  // was not unique.

  return (
    <div
      data-testid={base}
      className="flex flex-col gap-1"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2">
        <RunChip view={view} onPress={press} testId={`${base}-chip`} />
        {/* THE SECOND CHIP EXISTS ONLY WHERE THE STATE COULD NOT BE READ AND
            THE RETRIES ARE SPENT. Two chips is not a nicer UI; it is the only
            honest one there, because picking RUN or STOP for the owner would be
            claiming a state nothing answered for. */}
        {view.alsoOffers && (
          <RunChip
            view={{ ...view, label: view.alsoOffers === 'stop' ? 'STOP' : 'RUN',
                    action: view.alsoOffers, alsoOffers: null }}
            onPress={pressAlso}
            testId={`${base}-chip-alt`}
          />
        )}
        {line && (
          <span
            data-testid="agent-run-strip"
            className="text-[9px] font-bold uppercase tracking-wider leading-tight"
            style={{ color: STRIP_TONE[line.tone] ?? 'var(--rexy-muted, #666666)' }}
          >
            {line.line}
          </span>
        )}
      </div>
    </div>
  );
}


/**
 * THE LIVE LINE ALONE — no chip, for a surface that has a full row to give it.
 *
 * Mission Control's agent bar is a tight flex row; the round line belongs UNDER
 * it, where it can be a sentence. This holds the same refcounted poll every
 * other mount holds, so putting it on the page costs no extra read.
 */
export function RunStrip({ domain, agent }: { domain: string; agent: string | null }) {
  const { strip: line } = useAgentRun(domain, agent, true, false);
  if (!line) return null;
  return (
    <div
      data-testid="agent-run-strip-row"
      className="text-[9px] font-bold uppercase tracking-wider leading-tight"
      style={{ color: STRIP_TONE[line.tone] ?? 'var(--rexy-muted, #666666)' }}
    >
      {line.line}
    </div>
  );
}
